import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import ConfirmCancellationDialog from "features/general/ConfirmCancellationDialog";
import { ReactNode, useEffect, useState } from "react";
import { useBottomBarFormDialog } from "./useBottomBarFormDialog";

interface EventFormStep {
  label?: string;
  content: ReactNode;
}

interface EventFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  steps: EventFormStep[];
  formId?: string;
  onSave?: () => void;
  saveDisabled?: boolean;
  isDirty?: () => boolean;
  cancelAnalyticsEvent?: string;
  /** Called before advancing from step `index`. Return false to block navigation. */
  onValidateStep?: (index: number) => boolean;
}

export default function EventFormDialog({
  open,
  onClose,
  title,
  steps,
  formId,
  onSave,
  saveDisabled,
  isDirty,
  cancelAnalyticsEvent,
  onValidateStep,
}: EventFormDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [currentStep, setCurrentStep] = useState(0);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // Reset to step 0 when dialog reopens
  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  // Broadcast open state so BottomTabNavigation can show FABs for edit dialogs too
  useBottomBarFormDialog(open);

  const isMultiStep = steps.length > 1;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleCancel = () => {
    if (isDirty?.()) {
      setConfirmCancelOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    if (cancelAnalyticsEvent) {
      analytics.track(cancelAnalyticsEvent);
    }
    setConfirmCancelOpen(false);
    onClose();
  };

  const handleNext = () => {
    if (onValidateStep && !onValidateStep(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  // Build action buttons for the header
  const actionButtons: ReactNode[] = [];

  if (isMultiStep && !isFirstStep) {
    actionButtons.push(
      <Button
        key="back"
        color="secondary"
        data-action="dialog-back"
        onClick={handleBack}
        sx={{ display: { xs: "none", sm: "inline-flex" } }}
      >
        {t("edit.button.back")}
      </Button>
    );
  }

  if (isLastStep) {
    actionButtons.push(
      <Button
        key="save"
        variant="contained"
        disabled={saveDisabled}
        data-action="dialog-save"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          backgroundColor: "#2E7831",
          "&:hover": { backgroundColor: "#1C4321" },
          textTransform: "none",
          borderRadius: "8px",
          px: 3,
        }}
        {...(formId
          ? { type: "submit" as const, form: formId }
          : { onClick: onSave })}
      >
        {t("edit.button.save")}
      </Button>
    );
  } else {
    actionButtons.push(
      <Button
        key="next"
        color="secondary"
        data-action="dialog-next"
        onClick={handleNext}
        sx={{ display: { xs: "none", sm: "inline-flex" } }}
      >
        {t("edit.button.next")}
      </Button>
    );
  }

  // Cancel button (hidden, for mobile FABs to find)
  if (isMultiStep && !isFirstStep) {
    // No cancel button needed — Back is the left action, X handles cancel
  } else {
    actionButtons.push(
      <Button
        key="cancel"
        data-action="dialog-cancel"
        onClick={handleCancel}
        sx={{ display: "none" }}
      >
        {t("edit.button.cancel")}
      </Button>
    );
  }

  return (
    <>
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
        open={open}
        onClose={handleCancel}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(8px)",
          },
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #F0F0F0",
            backgroundColor: "white",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "text.primary",
              flex: 1,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {actionButtons}
            <IconButton
              onClick={handleCancel}
              size="small"
              sx={{
                backgroundColor: "#F1EFF7",
                color: "#7D5DA3",
                "&:hover": { backgroundColor: "#E5E1F0" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            backgroundColor: { xs: "white", sm: "#F7FCF1" },
            p: { xs: 2, sm: 3 },
            pb: { xs: "100px", sm: 3 },
            // Input styling overrides — match Grass Wedge
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "white",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D3C9E7",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7d5da3",
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.85rem",
            },
          }}
        >
          {steps[currentStep].content}
        </DialogContent>
      </Dialog>
      <ConfirmCancellationDialog
        dialogOpen={confirmCancelOpen}
        closeDialog={() => setConfirmCancelOpen(false)}
        onConfirmCancel={handleConfirmCancel}
        onCancelCancel={() => setConfirmCancelOpen(false)}
      />
    </>
  );
}
